import * as THREE from 'three';
import type { Balloon, Celebration, Effect } from '../../shared/protocol.ts';
import type { PublicPartyConfig } from '../../shared/config.ts';
import { sampleBalloon } from '../../shared/physics.ts';
import { cube, matte } from './assets.ts';
export { PartyAudio } from '../audio/party-audio.ts';

const colors=['#ffcb57','#ff6f98','#ae85f5','#65d7c4','#fff2c5','#7dbfff'].map(c=>new THREE.Color(c));
function random(seed:number,index:number){let x=Math.imul(seed^(index+1),1597334677);x=Math.imul(x^(x>>>16),2246822519);return ((x^(x>>>13))>>>0)/4294967296;}
const balloonParts=[
  [0,0,0,.47,.57,.47],[0,.018,0,.57,.39,.43],[0,.018,0,.43,.39,.57],
  [0,.31,0,.30,.075,.30],[0,-.315,0,.09,.09,.09],[-.15,.135,.241,.085,.18,.012],
  [0,-.495,0,.015,.28,.015],
];

/** Colors upload on membership changes, never on every animation frame. */
export class BalloonBatch {
  readonly mesh:THREE.InstancedMesh;
  private balloons:Balloon[]=[];
  private readonly transform=new THREE.Object3D();
  private readonly point={x:0,y:0,z:0};
  private readonly color=new THREE.Color();
  private nextExpiry=Infinity;
  constructor(private readonly config:PublicPartyConfig){
    this.mesh=new THREE.InstancedMesh(cube,matte,Math.max(1,config.abilities.maxBalloons)*balloonParts.length);
    this.mesh.name='Persistent party balloons';this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.castShadow=false;this.mesh.receiveShadow=false;this.mesh.frustumCulled=false;
    this.mesh.setColorAt(0,this.color);this.mesh.count=0;
  }
  sync(balloons:Balloon[]){
    this.balloons=balloons.slice(0,this.config.abilities.maxBalloons);this.nextExpiry=Infinity;
    this.balloons.forEach((balloon,i)=>{this.nextExpiry=Math.min(this.nextExpiry,balloon.expiresAt);
      balloonParts.forEach((_,part)=>this.mesh.setColorAt(i*balloonParts.length+part,this.color.set(part===5?'#fff1db':part===6?'#b29981':balloon.color)));
    });
    this.mesh.count=this.balloons.length*balloonParts.length;
    if(this.mesh.instanceColor){this.mesh.instanceColor.clearUpdateRanges();this.mesh.instanceColor.addUpdateRange(0,Math.max(1,this.mesh.count)*3);this.mesh.instanceColor.needsUpdate=true;}
  }
  update(now:number,reduced:boolean){
    if(now>=this.nextExpiry)this.sync(this.balloons.filter(b=>now<b.expiresAt));
    if(!this.mesh.count)return;
    let count=0;
    for(const balloon of this.balloons){
      sampleBalloon(balloon,now,this.config.physics,this.point);
      const lean=reduced?0:Math.sin((now-balloon.createdAt)*.003)*.025;
      const fade=balloon.releaseAt===undefined?1:Math.max(.001,Math.min(1,(balloon.expiresAt-now)/1200));
      for(const [x,y,z,w,h,d]of balloonParts){
        this.transform.position.set(this.point.x+x*fade,this.point.y+y*fade,this.point.z+z*fade);
        this.transform.rotation.set(0,0,lean);this.transform.scale.set(w*fade,h*fade,d*fade);
        this.transform.updateMatrix();this.mesh.setMatrixAt(count++,this.transform.matrix);
      }
    }
    this.mesh.instanceMatrix.clearUpdateRanges();this.mesh.instanceMatrix.addUpdateRange(0,count*16);this.mesh.instanceMatrix.needsUpdate=true;
  }
  dispose(){this.mesh.dispose();}
}

/** Bright unlit pixel fireworks: no shadow pass, post-processing, or per-particle objects. */
export class EffectPool {
  readonly mesh:THREE.InstancedMesh;
  private readonly material=new THREE.MeshBasicMaterial({color:0xffffff,toneMapped:false});
  private count=0;
  // Sized for the server cap: 6 concurrent shows x 4 bursts plus the celebration, pies and confetti.
  readonly maximum=2400;
  // Fixed per burst: recomputing it from the number of active bursts made live bursts pop.
  private readonly perBurst=56;
  constructor(){
    this.mesh=new THREE.InstancedMesh(cube,this.material,this.maximum);this.mesh.name='Pooled party particles';
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);this.mesh.frustumCulled=false;
    // Allocate color attributes before the first draw, so an empty initial pool has the right shader.
    this.mesh.setColorAt(0,colors[0]);this.mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);this.mesh.count=0;
  }
  private particle(x:number,y:number,z:number,size:number,color:THREE.Color,flat=1,rotation=0){
    if(this.count>=this.maximum||size<=0)return;
    const a=this.mesh.instanceMatrix.array,i=this.count*16,c=Math.cos(rotation),s=Math.sin(rotation);
    a[i]=c*size;a[i+1]=0;a[i+2]=-s*size;a[i+3]=0;
    a[i+4]=0;a[i+5]=size*flat;a[i+6]=0;a[i+7]=0;
    a[i+8]=s*size;a[i+9]=0;a[i+10]=c*size;a[i+11]=0;
    a[i+12]=x;a[i+13]=y;a[i+14]=z;a[i+15]=1;
    const rgb=this.mesh.instanceColor!.array,j=this.count*3;rgb[j]=color.r;rgb[j+1]=color.g;rgb[j+2]=color.b;
    this.count++;
  }
  private burst(x:number,z:number,start:number,now:number,seed:number,reduced:boolean){
    const age=(now-start)/1000;if(age<0||age>3.25)return;
    const height=2.65+random(seed,0)*.7;
    if(reduced){for(let i=0;i<5;i++){const angle=i/5*Math.PI*2;this.particle(x+Math.cos(angle)*.4,2.2+Math.sin(angle)*.4,z,.13,colors[((seed>>>0)+i)%colors.length]);}return;}
    if(age<.6){
      const y=.4+age/.6*height;this.particle(x,y,z,.16,colors[4]);
      for(let i=1;i<=4;i++)this.particle(x,y-i*.15,z,Math.max(.02,.105-i*.019),colors[(seed>>>0)%colors.length]);
      return;
    }
    const t=age-.6,damping=-Math.expm1(-t*.8)/.8;
    for(let i=0;i<this.perBurst;i++){
      const angle=random(seed,i*3+1)*Math.PI*2,vertical=random(seed,i*3+2)*2-1;
      const speed=1.2+random(seed,i*3+3)*1.65,radial=Math.sqrt(1-vertical*vertical);
      const y=height+vertical*speed*damping-.5*t*t;
      if(y>.15)this.particle(x+Math.cos(angle)*radial*speed*damping,y,z+Math.sin(angle)*radial*speed*damping,.135*Math.max(0,1-t/2.7),colors[((seed>>>0)+i)%colors.length],1,t+i);
    }
  }
  update(effects:Effect[],celebration:Celebration|null,now:number,reduced:boolean){
    this.count=0;
    // Pies and the celebration draw first, so extra fireworks cannot starve their visuals.
    const celebrating=!!celebration&&now>=celebration.fireworksAt&&now<celebration.endsAt;
    for(const e of effects)if(e.kind==='pie'&&e.target&&now>=e.startedAt&&now<e.impactAt){
      const t=Math.min(1,(now-e.startedAt)/(e.impactAt-e.startedAt));
      const x=e.origin.x+(e.target.x-e.origin.x)*t,z=e.origin.z+(e.target.z-e.origin.z)*t,y=1.07+Math.sin(t*Math.PI)*.94;
      this.particle(x,y,z,.39,colors[0],.22,t*6);this.particle(x,y+.05,z,.31,colors[4],.31,t*6);this.particle(x+.06,y+.115,z,.11,colors[1],.7,t*6);
    }
    if(celebrating&&celebration){
      const time=(now-celebration.fireworksAt)/1000,n=reduced?12:130;
      for(let i=0;i<n;i++){
        const delay=random(celebration.seed,i+1500)*2.2;if(time<delay)continue;
        const t=(time-delay)%4.5,angle=random(celebration.seed,i+2000)*Math.PI*2,radius=random(celebration.seed,i+2500)*3;
        this.particle(celebration.center.x+Math.cos(angle)*radius, reduced?.12:Math.max(.12,4.5-t),celebration.center.z+Math.sin(angle)*radius,.09,colors[i%colors.length],.22,reduced?0:t*2+i);
      }
      const latest=Math.floor(time/.68);
      for(let i=Math.max(0,latest-4);i<=latest;i++){
        const angle=random(celebration.seed,i)*Math.PI*2;
        this.burst(celebration.center.x+Math.cos(angle)*2.4,celebration.center.z+Math.sin(angle)*2.4,celebration.fireworksAt+i*680,now,celebration.seed+i*41,reduced);
      }
    }
    for(const e of effects){if(e.kind!=='fireworks'||now<e.startedAt||now>e.endsAt)continue;
      for(let i=0;i<4;i++)this.burst(e.origin.x+(random(e.seed,i)-.5)*1.1,e.origin.z+(random(e.seed,i+10)-.5)*1.1,e.startedAt+i*680,now,e.seed+i*17,reduced);
    }
    this.mesh.count=this.count;
    if(this.count){
      this.mesh.instanceMatrix.clearUpdateRanges();this.mesh.instanceMatrix.addUpdateRange(0,this.count*16);this.mesh.instanceMatrix.needsUpdate=true;
      this.mesh.instanceColor!.clearUpdateRanges();this.mesh.instanceColor!.addUpdateRange(0,this.count*3);this.mesh.instanceColor!.needsUpdate=true;
    }
  }
  dispose(){this.mesh.dispose();this.material.dispose();}
}
